import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  FolderKanban,
  CheckCircle2,
  Clock,
  Rocket,
  PauseCircle,
  ArrowRight,
  Cpu,
  Building2,
  Search,
  X
} from 'lucide-react';
import { cn } from '@/lib/utils';
import WebsiteLayout from '@/components/layout/WebsiteLayout';

const industryLabels = {
  insurtech: 'Insurtech',
  fintech: 'Fintech',
  oil_gas: 'Oil & Gas',
  telecom: 'Telecom',
  airline: 'Airline',
  others: 'Others',
};

const industryColors = {
  insurtech: 'bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300',
  fintech: 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300',
  oil_gas: 'bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300',
  telecom: 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300',
  airline: 'bg-cyan-100 text-cyan-700 border-cyan-200 dark:bg-cyan-900/30 dark:text-cyan-300',
  others: 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-700/30 dark:text-slate-300',
};

const statusConfig = {
  planning: { color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300', icon: Clock, label: 'Planning' },
  in_progress: { color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300', icon: Rocket, label: 'In Progress' },
  completed: { color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300', icon: CheckCircle2, label: 'Completed' },
  on_hold: { color: 'bg-gray-100 text-gray-700 dark:bg-gray-700/30 dark:text-gray-300', icon: PauseCircle, label: 'On Hold' },
};

// Skeleton loader for fast perceived loading
const ProjectSkeleton = () => (
  <Card className="border dark:border-slate-700 overflow-hidden animate-pulse">
    <div className="h-52 bg-slate-200 dark:bg-slate-700" />
    <CardContent className="p-6">
      <div className="h-6 bg-slate-200 dark:bg-slate-700 rounded w-3/4 mb-3" />
      <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/2 mb-4" />
      <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-full mb-2" />
      <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-2/3" />
    </CardContent>
  </Card>
);

// Project Card Component - Memoized for performance
const ProjectCard = ({ project, onClick }) => {
  const status = statusConfig[project.status] || statusConfig.planning;
  const StatusIcon = status.icon;
  const industryColor = industryColors[project.industry] || industryColors.others;

  return (
    <Card 
      className="border border-slate-700 hover:border-slate-600 hover:shadow-xl hover:shadow-blue-500/10 transition-all cursor-pointer overflow-hidden group bg-slate-800"
      onClick={() => onClick(project)}
    >
      <div className="h-52 bg-gradient-to-br from-blue-600 to-indigo-600 relative overflow-hidden">
        {project.featured_image ? (
          <img
            src={project.featured_image}
            alt={project.headline}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <FolderKanban className="w-16 h-16 text-white/30" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <Badge className={cn('absolute top-4 left-4', industryColor)}>
          <Building2 className="w-3 h-3 mr-1" />
          {industryLabels[project.industry] || 'Other'}
        </Badge>
        <Badge className={cn('absolute top-4 right-4', status.color)}>
          <StatusIcon className="w-3 h-3 mr-1" />
          {status.label}
        </Badge>
      </div>

      <CardContent className="p-6 bg-slate-800">
        <h3 className="text-xl font-bold text-white mb-3 line-clamp-2 group-hover:text-blue-400 transition-colors">
          {project.headline}
        </h3>
        
        <div className="flex items-center gap-2 text-sm text-slate-400 mb-4">
          <Cpu className="w-4 h-4 flex-shrink-0 text-blue-400" />
          <span className="line-clamp-1">{project.technology || 'Not specified'}</span>
        </div>

        <div 
          className="text-slate-400 text-sm line-clamp-3"
          dangerouslySetInnerHTML={{ __html: project.details?.substring(0, 200) || 'No description available.' }}
        />

        <div className="mt-4 pt-4 border-t border-slate-700">
          <span className="text-blue-400 text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
            View Details <ArrowRight className="w-4 h-4" />
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

export default function OurWorkPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [industryFilter, setIndustryFilter] = useState('all');
  const [selectedProject, setSelectedProject] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(9); // Initial 9 projects
  const LOAD_MORE_COUNT = 18; // Load 18 more each time

  // Fetch projects on mount
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await axios.get(`${API}/projects`);
        setProjects(response.data);
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  // Reset visible count when filters change
  useEffect(() => {
    setVisibleCount(9);
  }, [searchTerm, industryFilter]);

  // Memoized filtered projects for performance
  const filteredProjects = useMemo(() => {
    let filtered = projects;
    
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(p => 
        p.headline?.toLowerCase().includes(search) ||
        p.technology?.toLowerCase().includes(search) ||
        p.details?.toLowerCase().includes(search) ||
        industryLabels[p.industry]?.toLowerCase().includes(search)
      );
    }
    
    if (industryFilter !== 'all') {
      filtered = filtered.filter(p => p.industry === industryFilter);
    }
    
    return filtered;
  }, [projects, searchTerm, industryFilter]);

  // Visible projects (paginated)
  const visibleProjects = useMemo(() => {
    return filteredProjects.slice(0, visibleCount);
  }, [filteredProjects, visibleCount]);

  // Check if there are more projects to load
  const hasMore = visibleCount < filteredProjects.length;

  // Load more handler
  const handleLoadMore = useCallback(() => {
    setVisibleCount(prev => prev + LOAD_MORE_COUNT);
  }, []);

  // Memoized stats
  const stats = useMemo(() => ({
    total: projects.length,
    completed: projects.filter(p => p.status === 'completed').length,
    inProgress: projects.filter(p => p.status === 'in_progress').length,
  }), [projects]);

  // Callbacks
  const handleProjectClick = useCallback((project) => {
    setSelectedProject(project);
    setViewDialogOpen(true);
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);

  return (
    <WebsiteLayout>
      {/* Hero with Image */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950 relative overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 opacity-15">
          <img 
            src="https://images.unsplash.com/photo-1531482615713-2afd69097998?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwzfHxlbnRlcnByaXNlJTIwdGVjaG5vbG9neSUyMHRlYW0lMjB3b3JraW5nJTIwZGF0YSUyMGFuYWx5dGljcyUyMGRhc2hib2FyZHxlbnwwfHx8fDE3Nzk3ODE2MTJ8MA&ixlib=rb-4.1.0&q=85" 
            alt="Team working on project"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-blue-400 font-semibold mb-4 tracking-wide uppercase text-sm">Our Portfolio</p>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                Our Work
              </h1>
              <p className="text-xl text-slate-400 max-w-2xl">
                Explore our portfolio of successful AI and technology projects across various industries.
              </p>
            </div>
            <div className="hidden lg:block">
              <img 
                src="https://images.unsplash.com/photo-1769740333462-9a63bfa914bc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBtZWV0aW5nJTIwcm9vbSUyMHByZXNlbnRhdGlvbiUyMGNvcnBvcmF0ZSUyMG9mZmljZXxlbnwwfHx8fDE3Nzk3ODE2MTN8MA&ixlib=rb-4.1.0&q=85" 
                alt="Team meeting"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Filter */}
      <section className="py-8 bg-slate-800 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Search projects by name, technology, or industry..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="pl-10 h-12 text-base bg-slate-900 border-slate-600 text-white placeholder:text-slate-500 focus:border-blue-500"
              />
              {searchTerm && (
                <button 
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-700 rounded-full"
                >
                  <X className="w-4 h-4 text-slate-400" />
                </button>
              )}
            </div>
            
            <Select value={industryFilter} onValueChange={setIndustryFilter}>
              <SelectTrigger className="w-full sm:w-56 h-12 bg-slate-900 border-slate-600 text-white">
                <SelectValue placeholder="Filter by industry" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="all" className="text-white hover:bg-slate-700">All Industries</SelectItem>
                {Object.entries(industryLabels).map(([value, label]) => (
                  <SelectItem key={value} value={value} className="text-white hover:bg-slate-700">
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <p className="text-slate-400 mt-4 text-sm">
            Showing {Math.min(visibleCount, filteredProjects.length)} of {filteredProjects.length} project{filteredProjects.length !== 1 ? 's' : ''}
            {searchTerm && ` matching "${searchTerm}"`}
          </p>
        </div>
      </section>

      {/* Projects */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <ProjectSkeleton key={i} />
              ))}
            </div>
          ) : filteredProjects.length > 0 ? (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {visibleProjects.map((project) => (
                  <ProjectCard 
                    key={project.id} 
                    project={project} 
                    onClick={handleProjectClick}
                  />
                ))}
              </div>
              
              {/* Load More Button */}
              {hasMore && (
                <div className="text-center mt-12">
                  <Button
                    onClick={handleLoadMore}
                    size="lg"
                    className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-8 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all"
                    data-testid="load-more-btn"
                  >
                    Load More Projects
                    <span className="ml-2 text-blue-200">
                      ({visibleCount} of {filteredProjects.length})
                    </span>
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-16">
              <FolderKanban className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No projects found</h3>
              <p className="text-slate-400">
                {industryFilter !== 'all' || searchTerm ? 'Try adjusting your filters' : 'Check back soon for our latest work'}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-indigo-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Ready to Start Your Project?</h2>
          <p className="text-xl text-blue-100 mb-8">Let&apos;s discuss how we can help transform your business with AI.</p>
          <a href="/#contact">
            <Button size="lg" variant="secondary" className="text-blue-600 hover:text-blue-700">
              Get in Touch <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </a>
        </div>
      </section>

      {/* Project Detail Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="text-2xl pr-8 text-slate-900">{selectedProject?.headline}</DialogTitle>
          </DialogHeader>
          {selectedProject && (
            <div className="space-y-6">
              {selectedProject.featured_image && (
                <div className="rounded-xl overflow-hidden">
                  <img
                    src={selectedProject.featured_image}
                    alt={selectedProject.headline}
                    className="w-full h-64 object-cover"
                  />
                </div>
              )}

              <div className="flex flex-wrap items-center gap-3">
                <Badge className={cn(industryColors[selectedProject.industry] || industryColors.others)}>
                  <Building2 className="w-3 h-3 mr-1" />
                  {industryLabels[selectedProject.industry] || 'Other'}
                </Badge>
                <Badge className={cn(statusConfig[selectedProject.status]?.color)}>
                  {statusConfig[selectedProject.status]?.label || 'Planning'}
                </Badge>
              </div>

              <div className="bg-slate-50 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-2">
                  Technology Stack
                </h4>
                <div className="flex items-center gap-2 text-slate-900">
                  <Cpu className="w-5 h-5 text-blue-600" />
                  <span>{selectedProject.technology || 'Not specified'}</span>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">
                  Project Details
                </h4>
                <div 
                  className="prose prose-slate max-w-none text-slate-700"
                  dangerouslySetInnerHTML={{ __html: selectedProject.details || 'No details available.' }}
                />
              </div>

              <div className="pt-4 border-t border-slate-200">
                <a href="/#contact">
                  <Button className="w-full bg-blue-600 hover:bg-blue-700">
                    Start a Similar Project <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </WebsiteLayout>
  );
}
